import { useState, useEffect, useCallback, useRef } from 'react';

const defaultSettings = {
    enableHighAccuracy: false,
    timeout: 10000,
    maximumAge: 60000,
};

const isFirefox = () => {
    return navigator.userAgent.toLowerCase().includes('firefox');
};

export const usePosition = () => {

    const [position, setPosition] = useState({});
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [permissionState, setPermissionState] = useState('prompt');
    const [hasPosition, setHasPosition] = useState(false);

    const onChange = ({ coords, timestamp }) => {
        console.log('Position received:', { lat: coords.latitude, lng: coords.longitude });
        setPosition({
            latitude: coords.latitude,
            longitude: coords.longitude,
            accuracy: coords.accuracy,
            speed: coords.speed,
            heading: coords.heading,
            timestamp,
        });
        setHasPosition(true);
        setIsLoading(false);
        setPermissionState('granted');
        setError(null);
    };

    const onError = (error) => {
        console.log('Position error:', error.code, error.message);
        setError(error.message);
        setIsLoading(false);
        setHasPosition(false);
        if (error.code === error.PERMISSION_DENIED) {
            setPermissionState('denied');
        } else if (error.code === error.POSITION_UNAVAILABLE) {
            setPermissionState('granted');
            setError('Posición no disponible temporalmente');
        } else if (error.code === error.TIMEOUT) {
            setError('Timeout obteniendo ubicación');
        }
    };

    const requestPosition = () => {
        setIsLoading(true);
        setError(null);
        navigator.geolocation.getCurrentPosition(onChange, onError, defaultSettings);
    };

    // Función específica para manejar Firefox después de aceptar permisos
    const handleFirefoxPermissionAccepted = useCallback(() => {
        if (isFirefox()) {
            console.log('Firefox detected, implementing workaround for permission bug');
            
            // Delay para permitir que Firefox procese el permiso
            setTimeout(() => {
                if (navigator?.geolocation) {
                    navigator.geolocation.getCurrentPosition(
                        (position) => {
                            onChange(position);
                            // Forzar un refresh adicional para asegurar que el estado se mantenga
                            setTimeout(() => {
                                setPermissionState('granted');
                                setHasPosition(true);
                                console.log('Firefox permission workaround completed');
                            }, 500);
                        },
                        (error) => {
                            console.log('Firefox workaround error:', error);
                            onError(error);
                        },
                        { ...defaultSettings, timeout: 15000 }
                    );
                }
            }, 1000);
        }
    }, []);

    const checkPermission = async () => {
        console.log('Checking permissions...');
        
        if (!navigator?.geolocation) {
            console.log('Geolocation not supported');
            setPermissionState('denied');
            setError('Geolocation no soportado en este navegador');
            setIsLoading(false);
            setHasPosition(false);
            return;
        }

        if ('permissions' in navigator) {
            try {
                const result = await navigator.permissions.query({ name: 'geolocation' });
                console.log('Permission state:', result.state);
                setPermissionState(result.state);
                
                // Escuchar cambios en los permisos
                result.addEventListener('change', () => {
                    console.log('Permission changed to:', result.state);
                    setPermissionState(result.state);
                    if (result.state === 'granted') {
                        requestPosition();
                    } else if (result.state === 'denied') {
                        setError('Permiso de ubicación denegado');
                        setIsLoading(false);
                        setHasPosition(false);
                    }
                });
                
                if (result.state === 'granted') {
                    console.log('Permissions granted, requesting position...');
                    requestPosition();
                } else if (result.state === 'denied') {
                    console.log('Permissions denied');
                    setError('Permiso de ubicación denegado');
                    setIsLoading(false);
                    setHasPosition(false);
                } else {
                    console.log('Permissions prompt, waiting for user action');
                    setError('Para habilitar tu registro de asistencia activa tu ubicación.');
                    setIsLoading(false);
                    setHasPosition(false);
                }
            } catch (err) {
                console.log('Permission query failed, trying direct request:', err);
                requestPosition();
            }
        } else {
            console.log('Permission API not available, trying direct request');
            requestPosition();
        }
    };

    useEffect(() => {
        console.log('Hook mounted, checking permissions...');
        checkPermission();
    }, []);

    const refreshPosition = () => {
        if (navigator?.geolocation) {
            requestPosition();
        }
    };

    const requestLocationPermission = () => {
        console.log('Requesting location permission...');
        setError(null);
        
        if (isFirefox()) {
            console.log('Firefox detected, using enhanced permission request');
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    onChange(position);
                    handleFirefoxPermissionAccepted();
                },
                onError,
                defaultSettings
            );
        } else {
            requestPosition();
        }
    };

    // Estados derivados para facilitar el uso
    const hasPermission = permissionState === 'granted';
    const needsPermission = permissionState === 'prompt';
    const permissionDenied = permissionState === 'denied';

    return { 
        ...position, 
        error, 
        isLoading, 
        permissionState, 
        hasPosition, 
        hasPermission,
        needsPermission,
        permissionDenied,
        requestPosition,
        refreshPosition,
        requestLocationPermission,
        isFirefox: isFirefox(),
        handleFirefoxPermissionAccepted
    };
};